/**
 * Zephyrwrite Cloud API base URL (#370).
 *
 * `VITE_CLOUD_API_URL` wins when set. Otherwise production builds fall back to
 * the production cloud and development builds to the local Phoenix default,
 * the same rule `src-tauri/build.rs` (`cloud_api_url`) applies on the Rust
 * side, so a release build can never sign in against a local port.
 */

import { assertNever } from "./assertNever";
import { matchMaybe, type Maybe } from "./maybe";
import { trimmedMaybe } from "./trimmedString";

export const PRODUCTION_CLOUD_API_URL = "https://app.zephyrwrite.com";
export const DEVELOPMENT_CLOUD_API_URL = "http://localhost:4000";

export type CloudBuildMode = "production" | "development";

/** Default origin when no URL is configured for this build mode. */
export function fallbackCloudApiBase(mode: CloudBuildMode): string {
  switch (mode) {
    case "production":
      return PRODUCTION_CLOUD_API_URL;
    case "development":
      return DEVELOPMENT_CLOUD_API_URL;
    default:
      return assertNever(mode);
  }
}

/** Pure resolver: configured URL (trailing slash dropped) or the mode fallback. */
export function resolveCloudApiBase(
  configured: Maybe<string>,
  mode: CloudBuildMode,
): string {
  return matchMaybe(configured, {
    just: (url) => url.replace(/\/$/, ""),
    nothing: () => fallbackCloudApiBase(mode),
  });
}

/** `import.meta.env.PROD` → build mode. */
export function cloudBuildMode(prod: boolean): CloudBuildMode {
  return prod ? "production" : "development";
}

/** Build mode of the running bundle. */
export function currentCloudBuildMode(): CloudBuildMode {
  return cloudBuildMode(import.meta.env.PROD === true);
}

/** `VITE_CLOUD_API_URL` baked into this bundle, if any. */
export function configuredCloudApiUrl(): Maybe<string> {
  return trimmedMaybe(String(import.meta.env.VITE_CLOUD_API_URL ?? ""));
}

/** Cloud API base URL for this bundle (no trailing slash). */
export function cloudApiBaseUrl(): string {
  return resolveCloudApiBase(configuredCloudApiUrl(), currentCloudBuildMode());
}
